import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Paper,
  Box,
  Button,
  Tabs,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  ListSubheader,
  Typography,
} from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import StaticAnalysis, {
  ChartDataProvider,
} from "./projectDetails/StaticAnalysis";
import StyledTab from "./common/StyledTab";
import Layout from "./Layout";
const IssueProjectDetails = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const token = localStorage.getItem("access_token");
  const organizationId = localStorage.getItem("organization_id");
  const newRunId = useRef(uuidv4());

  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(location.state?.activeTab ?? 1);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredProjects = projects.filter((project) =>
    project.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    if (!organizationId) return;

    const fetchAll = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/projects?organization_id=${organizationId}`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const data = await res.json();
        setProjects(data.projects_list || []);
      } catch (err) {
        console.error("Error fetching projects:", err);
      }
    };

    fetchAll();
  }, [organizationId, token]);

  const fetchProjectDetails = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/projects/${projectId}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await res.json();
      setProject(data);
    } catch (err) {
      console.error("Error fetching project details:", err);
    } finally {
      setLoading(false);
    }
  }, [projectId, token]);

  useEffect(() => {
    fetchProjectDetails();
  }, [fetchProjectDetails]);

  const handleTabChange = (_, newVal) => {
    setActiveTab(newVal);
  };

  const handleProjectChange = (e) => {
    const selectedProjId = e.target.value;
    const selectedProj = projects.find((p) => p.project_id === selectedProjId);
    navigate(`/projects/issues/${selectedProjId}`, {
      state: {
        activeTab: 1,
        projectName: selectedProj?.name,
      },
    });
  };

  return (
    <Layout>
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            rowGap: 2,
            mb: 3,
          }}
        >
          <FormControl sx={{ minWidth: 300 }}>
            <InputLabel id="project-select-label">Select Project</InputLabel>
            <Select
              labelId="project-select-label"
              value={projectId || ""}
              label="Select Project"
              onChange={handleProjectChange}
              size="small"
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 400,
                  },
                },
              }}
            >
              {/* Search bar as ListSubheader */}
              <ListSubheader disableSticky>
                <TextField
                  autoFocus
                  placeholder="Search projects..."
                  size="small"
                  fullWidth
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </ListSubheader>

              {filteredProjects.map((p) => (
                <MenuItem
                  key={p.project_id}
                  value={p.project_id}
                  title={p.name}
                  sx={{
                    maxWidth: 280,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "50vh",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="h4" sx={{ mb: 2 }}>
              {project?.name || "No project selected"}
            </Typography>

            {activeTab === 1 && projectId && (
              <ChartDataProvider projectId={projectId}>
                <StaticAnalysis />
              </ChartDataProvider>
            )}
          </>
        )}
      </Paper>
    </Box>
    </Layout>
  );
};

export default IssueProjectDetails;
